/**
 * Language-aware internal URLs.
 *
 * Part of the route tree lives under a `/<lang>` path prefix (default lang =
 * no prefix — see `App.tsx`).  Pages under those roots read `lang` from the
 * URL and force `i18n.changeLanguage()` on mount.  That makes a hardcoded
 * `/guide` link a language reset: a Russian visitor clicking it lands on the
 * English article and the whole UI flips to English behind them.
 *
 * Every internal link into one of these families must therefore go through
 * `localizePath()`, and the language switcher must rewrite the current URL
 * instead of only touching i18n state.
 */

import { DEFAULT_LANG, SUPPORTED_LANGS, type Lang } from './config';

/**
 * First path segment of every route family that has a `/<lang>` twin.
 * Keep in sync with `web/src/App.tsx` — a root missing here silently
 * degrades to an English-only link.
 */
export const LANG_PREFIXED_ROOTS = [
  'countries',
  'topics',
  'guide',
  'report',
  'refund',
  'map',
] as const;

export function isLangSegment(segment: string | undefined): segment is Lang {
  return !!segment && (SUPPORTED_LANGS as readonly string[]).includes(segment);
}

/** Split `/ru/guide/reference` → `{ lang: 'ru', path: '/guide/reference' }`. */
export function stripLangPrefix(pathname: string): { lang: Lang; path: string } {
  const segments = pathname.split('/').filter(Boolean);
  if (isLangSegment(segments[0])) {
    return { lang: segments[0], path: `/${segments.slice(1).join('/')}` };
  }
  return { lang: DEFAULT_LANG, path: `/${segments.join('/')}` };
}

/** True when the path belongs to a route family that has a `/<lang>` twin. */
export function isLangPrefixedPath(pathname: string): boolean {
  const { path } = stripLangPrefix(pathname);
  const root = path.split('/').filter(Boolean)[0];
  return !!root && (LANG_PREFIXED_ROOTS as readonly string[]).includes(root);
}

/**
 * Rewrite an internal path for `lang`.  Paths outside the prefixed families
 * (`/quiz`, `/privacy`, `/it/map`, …) and external/relative URLs are returned
 * untouched — those pages take their language from i18n state alone.
 *
 * Query string and hash are preserved.
 */
export function localizePath(target: string, lang: Lang): string {
  if (!target.startsWith('/')) return target;
  const cut = target.search(/[?#]/);
  const pathname = cut === -1 ? target : target.slice(0, cut);
  const suffix = cut === -1 ? '' : target.slice(cut);

  if (!isLangPrefixedPath(pathname)) return target;

  const { path } = stripLangPrefix(pathname);
  const prefix = lang === DEFAULT_LANG ? '' : `/${lang}`;
  return `${prefix}${path}${suffix}`;
}
